/**
 * Tiny i18n layer: English + Korean strings, a live language switch, and helpers
 * that re-apply translations to DOM elements when the language changes.
 *
 * Static labels register with `onLang(...)` (run now and on every switch).
 * Dynamic text (re-rendered each frame) just calls `t(key, params)`.
 */

import { isMac } from '../app/env';
import lessonCopy from '../tutorial/lessonCopy.json';

export type Lang = 'en' | 'ko';

/** The stroke modifier key's on-screen label — ⌘ on macOS (see App.ts's isStrokeModKey),
 *  Ctrl everywhere else. Any dict string may use `{mod}` to pick it up automatically. */
const MOD_KEY = isMac() ? '⌘' : 'Ctrl';

const DICT: Record<string, Record<Lang, string>> = {
  // menu
  'menu.game': { en: 'Game', ko: '게임' },
  'menu.mode': { en: 'Mode', ko: '모드' },
  'menu.handicap': { en: 'Handicap', ko: '다마' },
  'menu.theme': { en: 'Theme', ko: '테마' },
  'menu.env': { en: 'Room', ko: '공간' },
  'menu.language': { en: 'Language', ko: '언어' },
  'menu.settings': { en: 'Settings', ko: '설정' },
  'presence.title': { en: 'Players Online', ko: '접속자' },
  'presence.drag': { en: 'Drag to move', ko: '드래그해서 이동' },
  'presence.you': { en: 'you', ko: '나' },
  'presence.empty': { en: 'No one online', ko: '접속자 없음' },
  'presence.standby': { en: 'Standby', ko: '대기 중' },
  'presence.solo': { en: 'In-game (Solo)', ko: '게임 중 (혼자)' },
  'presence.ai': { en: 'In-game (vs AI)', ko: '게임 중 (AI)' },
  'presence.tutorial': { en: 'In tutorial', ko: '튜토리얼 중' },
  'presence.waiting': { en: 'In a room', ko: '방 대기 중' },
  'presence.spectating': { en: 'Spectating', ko: '관전 중' },
  'presence.playing1v1': { en: 'In-game (1v1)', ko: '게임 중 (1v1)' },
  'presence.playing2v2': { en: 'In-game (2v2)', ko: '게임 중 (2v2)' },
  'presence.playing1v1v1': { en: 'In-game (1v1v1)', ko: '게임 중 (1v1v1)' },
  'presence.playing1v1v1v1': { en: 'In-game (1v1v1v1)', ko: '게임 중 (1v1v1v1)' },
  'env.hall': { en: 'Billiard Hall', ko: '당구장' },
  'env.beach': { en: 'LA Beach (360°)', ko: 'LA 해변 (360°)' },
  'env.billiardhall': { en: 'Billiard Hall (3D model)', ko: '당구장 (3D 모델)' },
  'mode.solo': { en: 'Solo practice', ko: '혼자 연습' },
  'mode.ai': { en: 'Vs AI', ko: 'AI 대전' },
  'mode.online': { en: 'Online', ko: '온라인' },
  'variant.straight-rail': { en: '4 Ball', ko: '사구' },
  'variant.three-cushion': { en: '3 Cushion', ko: '삼구' },
  'theme.green': { en: 'Tournament Green', ko: '토너먼트 그린' },
  'theme.blue': { en: 'Tournament Blue', ko: '토너먼트 블루' },
  'theme.red': { en: 'Burgundy', ko: '버건디' },
  'theme.charcoal': { en: 'Charcoal', ko: '차콜' },
  'btn.newGame': { en: 'New Game', ko: '새 게임' },
  'btn.reviewShots': { en: 'Review shots', ko: '샷 리뷰' },
  'btn.rePractice': { en: 'Re-practice', ko: '다시 연습' },
  'btn.guideOn': { en: 'Aim Guide: On', ko: '조준선: 켜짐' },
  'btn.guideOff': { en: 'Aim Guide: Off', ko: '조준선: 꺼짐' },

  // controls
  'ctrl.spin': { en: 'Spin (drag the ball)', ko: '회전 (당점 드래그)' },
  'ctrl.aimPad': { en: 'Aim (drag)', ko: '조준 패드 (드래그)' },
  'ctrl.chalk': { en: 'Chalk (press C)', ko: '초크 (C 키)' },
  'btn.chalk': { en: 'Chalk', ko: '초크' },
  'btn.aimBoost': { en: 'Hold: 2x aim speed', ko: '누르는 동안 조준 2배' },
  'ctrl.power': { en: 'Power', ko: '세기' },
  'ctrl.pullShoot': { en: 'Pull down & release to shoot', ko: '당겨서 놓으면 샷' },
  'ctrl.tilt': { en: 'Cue tilt: {d}°', ko: '큐 기울기: {d}°' },
  'ctrl.scrub': { en: 'Scrub last shot', ko: '구간 이동' },
  'btn.shoot': { en: 'Shoot', ko: '치기' },
  'btn.replay': { en: 'Replay last shot', ko: '다시보기' },
  'cam.aim': { en: 'Aim', ko: '조준' },
  'cam.top': { en: 'Top', ko: '탑뷰' },
  'cam.orbit': { en: 'Orbit', ko: '자유' },
  'cam.action': { en: 'Action', ko: '액션' },

  // status / scoreboard (params: n, a, b, i, r, h, name)
  'status.soloSub': { en: 'Solo · clear {n}', ko: '혼자 · 목표 {n}점' },
  'status.handicapSub': { en: 'Korean handicap {a} / {b}', ko: '핸디캡 {a} / {b}' },
  'status.targets': { en: 'Handicap {list}', ko: '핸디캡 {list}' },
  'status.finalCushion': { en: '{name}: {c} more 3-cushion carom(s) to win', ko: '{name}: 마지막 3쿠션 {c}개 남음' },
  'status.finalCushionBanks': { en: '{name}: {c} 3-cushion ({b} 가락) to win', ko: '{name}: 마지막 3쿠션 {c}개 (가락 {b}개) 남음' },
  'score.left': { en: 'to go', ko: '남음' },
  'score.finalCushion3': { en: '3-cush', ko: '3쿠션' },
  'score.finalBank': { en: 'bank', ko: '가락' },
  'banner.cleared': { en: 'Cleared!', ko: '성공!' },
  'banner.wins': { en: '{name} wins!', ko: '{name} 승리!' },
  'banner.draw': { en: 'Draw!', ko: '무승부!' },
  'banner.standings': { en: 'Final standings', ko: '최종 순위' },
  'ord.1': { en: '1st', ko: '1등' },
  'ord.2': { en: '2nd', ko: '2등' },
  'ord.3': { en: '3rd', ko: '3등' },
  'ord.4': { en: '4th', ko: '4등' },
  'placement.you': { en: 'You finished {place}!', ko: '{place}으로 마쳤습니다!' },
  'placement.other': { en: '{name} finished {place}', ko: '{name} 님 {place}' },
  'banner.again': { en: 'New Game to play again', ko: '새 게임으로 다시 시작' },

  // hint
  'hint': {
    en: '<b>Left-click</b> to aim, then <b>move</b> to aim (left/right) and tilt (up/down) · <b>hold left</b> + drag to set spin · <b>right-click</b> exits aim, holding the angle · out of aim: <b>right-drag</b> orbits, <b>middle-drag</b> pans, <b>wheel</b> zooms · <b>Shift</b> fine aim · hold <b>{mod}</b>, <b>pull back</b> for power then <b>push forward</b> to shoot · <b>G</b> guide · hold <b>F</b> to point at the table · <b>Enter</b> chat (<b>/w name</b> to whisper)',
    ko: '<b>좌클릭</b>으로 조준 후 <b>이동</b>해 조준(좌/우)·틸트(상/하) · <b>좌클릭 유지</b> 드래그로 당점 · <b>우클릭</b>으로 조준 종료(각도 유지) · 조준 밖: <b>우클릭 드래그</b> 회전, <b>휠클릭 드래그</b> 이동, <b>휠</b> 줌 · <b>Shift</b> 정밀 조준 · <b>{mod}</b>을 누르고 <b>뒤로 당겨</b> 세기 후 <b>앞으로 밀어</b> 치기 · <b>G</b> 조준선 · <b>F</b> 유지로 테이블 가리키기 · <b>Enter</b> 채팅 (<b>/w 닉네임</b> 귓속말)',
  },

  // lobby
  'lobby.title': { en: 'Online Play', ko: '온라인 플레이' },
  'lobby.quick': { en: 'Join online', ko: '온라인 참가' },
  'lobby.searching': { en: 'Looking for an opponent…', ko: '상대를 찾는 중…' },
  'lobby.create': { en: 'Create room', ko: '방 만들기' },
  'lobby.or': { en: 'or', ko: '또는' },
  'lobby.codePlaceholder': { en: 'Room code', ko: '방 코드' },
  'lobby.join': { en: 'Join', ko: '입장' },
  'lobby.back': { en: 'Back to offline', ko: '오프라인으로' },
  'lobby.intro': { en: 'Create a room and share the code, or join one.', ko: '방을 만들어 코드를 공유하거나 입장하세요.' },
  'lobby.code': { en: 'Room code: {code}', ko: '방 코드: {code}' },
  'lobby.connecting': { en: 'Connecting…', ko: '연결 중…' },
  'lobby.connectFail': { en: 'Connection failed — is the server running? (npm run server)', ko: '연결 실패 — 서버가 실행 중인가요? (npm run server)' },
  'lobby.noResponse': { en: 'Server not responding — restart it (it may be an old version). Stop and re-run npm run dev.', ko: '서버가 응답하지 않습니다 — 재시작하세요 (구버전일 수 있음). npm run dev를 중지 후 다시 실행하세요.' },
  'lobby.waiting': { en: 'Waiting for an opponent to join…', ko: '상대를 기다리는 중…' },
  'lobby.spectating': { en: 'Spectating room {code}…', ko: '{code} 방 관전 중…' },
  'lobby.notFound': { en: 'Room not found', ko: '방을 찾을 수 없습니다' },
  // room browser + waiting room
  'lobby.nick': { en: 'Name', ko: '이름' },
  'lobby.nickPlaceholder': { en: 'Nickname (or play anonymously)', ko: '닉네임 (또는 익명 플레이)' },
  'lobby.roomNamePlaceholder': { en: 'Room name', ko: '방 이름' },
  'lobby.openRooms': { en: 'Open rooms', ko: '열린 방' },
  'lobby.createRoom': { en: '+ Create room', ko: '+ 방 만들기' },
  'lobby.empty': { en: '(empty)', ko: '(빈 방)' },
  'lobby.reroll': { en: 'New random name', ko: '새 이름 생성' },
  'lobby.createTitle': { en: 'Create a room', ko: '방 만들기' },
  'lobby.variant': { en: 'Game', ko: '종목' },
  'lobby.cancel': { en: 'Cancel', ko: '취소' },
  'lobby.toMenu': { en: '← Back', ko: '← 돌아가기' },
  'lobby.refresh': { en: 'Refresh', ko: '새로고침' },
  'lobby.noRooms': { en: 'No open rooms yet — create one above.', ko: '아직 열린 방이 없습니다 — 위에서 만들어 보세요.' },
  'lobby.noRoomsMatch': { en: 'No rooms match your filters.', ko: '조건에 맞는 방이 없습니다.' },
  'lobby.searchPlaceholder': { en: 'Search room or player…', ko: '방 제목·닉네임 검색…' },
  'lobby.allVariants': { en: 'All games', ko: '전체 종목' },
  'lobby.allFormats': { en: 'All formats', ko: '전체 형식' },
  'lobby.prevPage': { en: '‹ Prev', ko: '‹ 이전' },
  'lobby.nextPage': { en: 'Next ›', ko: '다음 ›' },
  'lobby.pageInfo': { en: '{from}–{to} of {total}', ko: '{total}개 중 {from}–{to}' },
  'lobby.playing': { en: 'playing', ko: '게임 중' },
  'lobby.waitingShort': { en: 'waiting', ko: '대기 중' },
  'lobby.watch': { en: 'Watch', ko: '관전' },
  'lobby.roomGone': { en: 'That room just filled up.', ko: '그 방은 방금 꽉 찼어요.' },
  'lobby.topPlayers': { en: 'Top players', ko: '랭킹 TOP 10' },
  'lobby.noRanks': { en: 'No ranked players yet.', ko: '랭킹 정보가 아직 없습니다.' },
  'lobby.levelFixed': { en: 'Online games are played at your own 다마', ko: '온라인은 내 다마로만 플레이됩니다' },
  'lobby.stake': { en: '코인 stake', ko: '코인 내기' },
  'lobby.stakeOff': { en: 'No stake', ko: '내기 없음' },
  'lobby.stakeAmount': { en: '{n} 코인', ko: '{n} 코인' },
  'lobby.stakeHint': { en: 'The loser pays this to the winner when the game ends.', ko: '게임이 끝나면 패자가 승자에게 이 금액을 지불합니다.' },
  'lobby.stakeWarn': { en: "Doesn't have enough 코인 to cover this stake", ko: '내기 코인이 부족합니다' },
  'lobby.stakeShort': { en: 'One or more players don’t have {n} 코인 for the stake', ko: '{n} 코인이 부족한 플레이어가 있습니다' },
  'lobby.stakeBadge': { en: '🪙 {n}', ko: '🪙 {n}' },
  'lobby.joinStakeShort': {
    en: 'This room bets {n} 코인 — you only have {have}. Win some games or visit the shop first.',
    ko: '{n} 코인 내기 방입니다 — 보유 코인은 {have} 코인이에요. 코인을 더 모은 뒤 참가할 수 있습니다.',
  },
  'lobby.stakeTooRich': { en: 'Not enough 코인 ({have})', ko: '코인 부족 (보유 {have})' },
  'lobby.rankGapBadge': { en: '±{n}점 only', ko: '±{n}점 이내' },
  'lobby.joinRankGap': {
    en: 'This room bets 코인, so only players within {n} tiers of the host ({tier}) can join.',
    ko: '코인 내기 방이라 호스트({tier}) 기준 ±{n}단계 이내 실력만 참가할 수 있습니다.',
  },
  'lobby.sectionMatch': { en: 'Match', ko: '경기 설정' },
  'lobby.sectionOpening': { en: 'Opening & finish', ko: '오프닝 · 마무리' },
  'lobby.sectionTable': { en: 'Table', ko: '테이블' },
  'lobby.tabRooms': { en: 'Rooms', ko: '방 목록' },
  'lobby.tabRanking': { en: 'Ranking', ko: '랭킹' },
  'lobby.signIn': { en: 'Sign in / sign up', ko: '로그인 · 가입' },
  'lobby.players': { en: 'Players', ko: '참가자' },
  'lobby.nickname': { en: 'Nickname', ko: '닉네임' },
  'lobby.password': { en: 'Password', ko: '비밀번호' },
  'lobby.email': { en: 'Email (for sign-up)', ko: '이메일 (가입용)' },
  'lobby.guestHint': { en: 'Playing as guest — sign up to keep this progress, or log in to switch to your existing account.', ko: '게스트로 플레이 중 — 가입하면 현재 기록이 그대로 이어지고, 로그인하면 기존 계정으로 전환됩니다.' },
  'lobby.login': { en: 'Log in', ko: '로그인' },
  'lobby.signup': { en: 'Sign up', ko: '가입' },
  'lobby.logout': { en: 'Log out', ko: '로그아웃' },
  'lobby.signedInAs': { en: 'Signed in as {name}', ko: '{name} 님으로 로그인됨' },
  'lobby.socialGoogle': { en: 'Continue with Google', ko: 'Google로 계속하기' },
  'lobby.socialKakao': { en: 'Continue with Kakao', ko: '카카오로 계속하기' },
  'lobby.socialOr': { en: 'or', ko: '또는' },
  'lobby.socialFailed': { en: 'Sign-in failed: {msg}', ko: '로그인 실패: {msg}' },
  'lobby.socialLinked': { en: 'Linked to your existing account.', ko: '기존 계정에 연결되었습니다.' },
  'lobby.socialClaimed': { en: 'Your guest progress carried over.', ko: '게스트 기록이 그대로 이어졌습니다.' },
  'lobby.authNeedBoth': { en: 'Enter a nickname and password.', ko: '닉네임과 비밀번호를 입력하세요.' },
  'authmodal.title': { en: 'Login / Sign up', ko: '로그인 / 회원가입' },
  'lobby.passwordConfirm': { en: 'Confirm password', ko: '비밀번호 확인' },
  'lobby.passwordMismatch': { en: 'Passwords do not match.', ko: '비밀번호가 일치하지 않습니다.' },
  'lobby.forgotPassword': { en: 'Forgot password?', ko: '비밀번호를 잊으셨나요?' },
  'lobby.forgotPasswordHint': { en: 'Contact an admin to reset your password.', ko: '비밀번호 재설정은 관리자에게 문의하세요.' },
  'admin.title': { en: 'Admin dashboard', ko: '관리자 대시보드' },
  'admin.summary': { en: '🛡 {online} online · {rooms} rooms', ko: '🛡 접속 {online} · 방 {rooms}개' },
  'admin.members': { en: 'Members', ko: '접속자' },
  'admin.rooms': { en: 'Rooms', ko: '방 목록' },
  'hud.rank': { en: 'Rank', ko: '순위' },
  'hud.level': { en: 'Level', ko: '다마' },
  'hud.winrate': { en: 'Win', ko: '승률' },
  'hud.coins': { en: 'Coins', ko: '코인' },
  'hud.exitSpectate': { en: 'Exit', ko: '관전 종료' },
  'hud.exitGame': { en: 'Exit', ko: '나가기' },
  'emote.configure': { en: 'Configure emotes', ko: '이모트 설정' },
  'emote.configureHint': { en: 'Pick a slot, then tap an emote to assign it. 🔊 = sound.', ko: '슬롯을 고른 뒤 이모트를 눌러 배치하세요. 🔊 = 소리.' },
  'emote.done': { en: 'Done', ko: '완료' },
  'admin.recentMatches': { en: 'Recent matches', ko: '최근 경기' },
  'admin.none': { en: 'None', ko: '없음' },
  'admin.setTier': { en: 'Set ladder tier (점)', ko: '랭크 급수 설정 (점)' },
  'lobby.format': { en: 'Format', ko: '형식' },
  'lobby.finish': { en: 'Finish (마지막 3쿠션)', ko: '마무리 (마지막 3쿠션)' },
  'lobby.finishCaroms': { en: 'Final 3-cushion caroms to win', ko: '마무리 쓰리쿠션 횟수' },
  'lobby.finishBanks': { en: 'Of those, bank (빈쿠션) shots', ko: '그중 뱅크(빈쿠션) 횟수' },
  'lobby.team': { en: 'Team {n}', ko: '{n}팀' },
  'lobby.spectators': { en: 'Spectators', ko: '관전자' },
  'lobby.you': { en: 'you', ko: '나' },
  'lobby.kick': { en: 'Remove from room', ko: '방에서 내보내기' },
  'lobby.ready': { en: 'Ready', ko: '준비' },
  'lobby.unready': { en: 'Not ready', ko: '준비 해제' },
  'lobby.items': { en: '🎒 Items', ko: '🎒 아이템' },
  'lobby.yourLevel': { en: 'Your level', ko: '내 다마' },
  'lobby.rankedGame': { en: 'Ranked', ko: '랭크 게임' },
  'lobby.tableColor': { en: 'Table color', ko: '테이블 색상' },
  'lobby.background': { en: 'Background', ko: '배경' },
  'lobby.change': { en: 'Change ▾', ko: '변경 ▾' },
  'lobby.opening': { en: 'Opening', ko: '오프닝' },
  'lobby.breakPreset': { en: 'Preset layout', ko: '기본 배치' },
  'lobby.breakSwing': { en: 'Cue swing break', ko: '큐 스윙 브레이크' },
  'lobby.start': { en: '▶ Start game', ko: '▶ 게임 시작' },
  'lobby.canStart': { en: 'Everyone ready — start when you are.', ko: '모두 준비 완료 — 시작하세요.' },
  'lobby.waitHost': { en: 'Waiting for the host to start…', ko: '방장이 시작하기를 기다리는 중…' },
  'lobby.leave': { en: 'Leave room', ko: '방 나가기' },
  'lobby.needPlayers': { en: 'Waiting for {n} more player(s)…', ko: '{n}명 더 기다리는 중…' },
  'lobby.waitReady': { en: 'Waiting for everyone to ready up…', ko: '모두 준비하기를 기다리는 중…' },
  'lobby.starting': { en: 'Everyone ready — starting!', ko: '모두 준비 완료 — 시작합니다!' },
  // main menu (start screen)
  'mainmenu.title': { en: '꾀꼬리 당구장', ko: '꾀꼬리 당구장' },
  'mainmenu.practice': { en: 'Single play (practice)', ko: '혼자 연습' },
  'mainmenu.vsai': { en: 'Single play (vs AI)', ko: 'AI 대전' },
  'mainmenu.online': { en: 'Online play', ko: '온라인 플레이' },
  'mainmenu.selectLevel': { en: 'Select your level', ko: '다마 선택' },
  'mainmenu.confirm': { en: 'Start', ko: '시작' },
  'mainmenu.back': { en: 'Back', ko: '뒤로' },
  'mainmenu.history': { en: 'Game history', ko: '게임 기록' },
  'mainmenu.onlineHint': { en: 'Rooms, ranked ladder, spectating', ko: '방 · 랭킹 · 관전' },
  'mainmenu.practiceHint': { en: 'Free table, no opponent', ko: '자유 연습' },
  'mainmenu.vsaiHint': { en: 'Play the computer at your 다마', ko: '내 다마로 컴퓨터와 대전' },
  'mainmenu.solo': { en: 'Single play', ko: '혼자 하기' },
  'mainmenu.guest': { en: 'Playing as guest', ko: '게스트로 플레이 중' },
  'menu.mainMenu': { en: 'Main menu', ko: '메인 메뉴' },
  // shot review / replay panel (params: n, name, power, p, result)
  'review.title': { en: 'Shot review', ko: '샷 리뷰' },
  'review.historyTitle': { en: 'Game replay', ko: '게임 다시보기' },
  'review.subtitleEnd': { en: 'Every shot of this game — replay or try a layout', ko: '이 게임의 모든 샷 — 다시보기 또는 배치 연습' },
  'review.subtitleHistory': { en: '{n} shots — replay or practice any', ko: '{n}개 샷 — 다시보기 또는 연습' },
  'review.shotN': { en: 'Shot {n}', ko: '{n}번 샷' },
  'review.scored': { en: 'Scored (+{p})', ko: '득점 (+{p})' },
  'review.miss': { en: 'Miss', ko: '실패' },
  'review.foul': { en: 'Foul', ko: '파울' },
  'review.caption': { en: 'Shot {n} · {name} · {power}% power · {result}', ko: '{n}번 샷 · {name} · 세기 {power}% · {result}' },
  'review.replay': { en: 'Replay', ko: '다시보기' },
  'review.tryLayout': { en: 'Try this layout', ko: '이 배치 연습' },
  'review.practiceShot': { en: 'Practice this shot', ko: '이 샷 연습' },
  'review.back': { en: 'Back to list', ko: '목록으로' },
  'review.empty': { en: 'No shots recorded', ko: '기록된 샷 없음' },
  'review.practiceMode': { en: 'Practice — anyone can shoot this layout', ko: '연습 — 누구나 이 배치로 칠 수 있어요' },
  'review.practiceLoaded': { en: 'Layout loaded — take the shot', ko: '배치 불러옴 — 샷을 쳐보세요' },
  // personal game history list (params: n, name)
  'history.title': { en: 'Game history', ko: '게임 기록' },
  'history.empty': { en: 'No games recorded yet', ko: '아직 기록된 게임이 없어요' },
  'history.review': { en: 'Review', ko: '리뷰' },
  'history.shots': { en: '{n} shots', ko: '{n}샷' },
  'history.won': { en: 'won by {name}', ko: '{name} 승' },
  'history.modeOnline': { en: 'Online', ko: '온라인' },
  'history.modeAi': { en: 'vs AI', ko: 'AI 대전' },
  'history.modeSolo': { en: 'Solo', ko: '혼자' },
  // item store (params: n)
  'mainmenu.store': { en: 'Item store', ko: '상점' },
  'store.title': { en: 'Item store', ko: '상점' },
  'store.balance': { en: '{n} 코인', ko: '{n} 코인' },
  'store.cat.all': { en: 'All', ko: '전체' },
  'store.cat.emote': { en: 'Emotes', ko: '이모트' },
  'store.cat.cueColor': { en: 'Cue colors', ko: '큐볼 색상' },
  'store.cat.music': { en: 'Music', ko: '음악' },
  'store.cat.other': { en: 'Other', ko: '기타' },
  'store.preview': { en: 'Preview sound', ko: '소리 미리듣기' },
  'store.buy': { en: 'Buy · {n}', ko: '구매 · {n}' },
  'store.owned': { en: 'Owned', ko: '보유중' },
  'store.comingSoon': { en: 'Coming soon', ko: '준비중' },
  'store.equip': { en: 'Equip', ko: '장착' },
  'store.equipped': { en: 'Equipped', ko: '장착중' },
  'store.play': { en: '▶ Play now', ko: '▶ 지금 재생' },
  'store.renamePrompt': { en: 'New nickname (2–20 characters):', ko: '새 닉네임 (2~20자):' },
  'store.confirmTitle': { en: 'Confirm purchase', ko: '구매 확인' },
  'store.confirmCurrent': { en: 'Current balance: {n} 코인', ko: '현재 잔액: {n} 코인' },
  'store.confirmAfter': { en: 'After purchase: {n} 코인', ko: '구매 후: {n} 코인' },
  'store.confirmCancel': { en: 'Cancel', ko: '취소' },

  // 건의하기 (one-way bug report / feedback)
  'mainmenu.feedback': { en: 'Report / Feedback', ko: '건의하기' },
  'feedback.title': { en: 'Report / Feedback', ko: '건의하기' },
  'feedback.messagePlaceholder': { en: 'Describe what happened, or what you’d like to see…', ko: '무슨 일이 있었는지, 어떤 걸 원하는지 적어주세요…' },
  'feedback.send': { en: 'Send', ko: '보내기' },
  'feedback.sent': { en: 'Thanks — sent to the team.', ko: '감사합니다 — 전달되었습니다.' },
  'emote.locked': { en: 'Locked — buy in the store', ko: '잠김 — 상점에서 구매' },

  // --- 점 ladder progress (params: n, a, b, t) ---
  'prog.title': { en: 'Your ladder', ko: '내 등급' },
  'prog.average': { en: 'Average', ko: '애버리지' },
  'prog.coinsLabel': { en: 'Coins', ko: '코인' },
  'prog.toPromote': { en: '{n} more to move up', ko: '승급까지 {n}판' },
  'prog.promoting': { en: 'Promotion cooking', ko: '승급 눈앞' },
  'prog.measuring': { en: 'Measuring — {a}/{b} innings', ko: '측정 중 — {a}/{b} 이닝' },
  'prog.atTop': { en: 'Top of the ladder', ko: '최상위 등급' },
  'prog.holding': { en: 'Holding {t}점', ko: '{t}점 유지 중' },
  'prog.demoWarn': { en: 'Below band — {n} more slips relegate', ko: '기준 미달 — {n}판 더 밀리면 강등' },
  'prog.next': { en: 'next {t}점', ko: '다음 {t}점' },

  // --- 코인 rewards (params: n) ---
  'earn.title': { en: 'Earned', ko: '획득' },
  'earn.innings': { en: '{n} innings played', ko: '{n} 이닝 플레이' },
  'earn.dailyWin': { en: 'First win of the day', ko: '오늘의 첫 승' },
  'earn.dailyClear': { en: "Cleared today's layout", ko: '오늘의 배치 성공' },
  'earn.dailyBest': { en: 'Beat your best', ko: '자기 기록 경신' },

  // --- 당구 교실 (tutorial) ---
  // Panel chrome + curriculum copy. Every mission carries four lines: what to do
  // (`goal`), which control does it (`keys`, and `touch` only where the gesture
  // genuinely differs), why it matters (`tip`), and what to say when an attempt
  // didn't get there (`retry`) — which is a nudge, never a scolding.
  'mainmenu.tutorial': { en: 'Tutorial', ko: '튜토리얼' },
  'mainmenu.tutorialHint': { en: 'Learn every control, one step at a time', ko: '컨트롤을 하나씩 배우기' },
  'mainmenu.courseChoose': { en: 'Choose what to learn', ko: '배울 과정을 선택하세요' },
  'mainmenu.courseComplete': { en: 'Complete', ko: '완료' },
  'mainmenu.courseIncomplete': { en: 'Incomplete', ko: '미완료' },
  'course.control.title': { en: 'Game control', ko: '게임 조작' },
  'course.control.hint': { en: 'Aim mode, the stroke, the aim guide', ko: '조준 모드, 스트로크, 조준선' },
  'course.skills.title': { en: 'Basic carom skills', ko: '당구 기본기' },
  'course.skills.hint': { en: 'Aim, pace, side spin, draw, follow, massé', ko: '조준, 세기, 옆회전, 끌어치기, 밀어치기, 마세' },
  'course.advanced.title': { en: 'Advanced carom skills', ko: '실전 응용' },
  'course.advanced.hint': { en: 'Bank shots and scoring a carom', ko: '빈쿠션과 득점' },
  'tut.title': { en: 'Billiards school', ko: '당구 교실' },
  'tut.chapter.basics': { en: 'The basics', ko: '기본기' },
  'tut.chapter.aim': { en: 'Aim and power', ko: '조준과 세기' },
  'tut.chapter.spin': { en: 'Spin', ko: '회전' },
  'tut.chapter.play': { en: 'Real shots', ko: '실전' },
  'tut.next': { en: 'Next step', ko: '다음 단계' },
  'tut.skip': { en: 'Skip', ko: '건너뛰기' },
  'tut.reset': { en: 'Reset balls', ko: '다시 배치' },
  'tut.exit': { en: 'Leave', ko: '나가기' },
  'tut.finish': { en: 'Finish', ko: '마치기' },
  'tut.passedNext': { en: 'Nicely done — moving on…', ko: '잘하셨어요! 다음 단계로 넘어갑니다…' },
  'tut.collapse': { en: 'Collapse', ko: '접기' },
  'tut.expand': { en: 'Expand', ko: '펼치기' },
  'tut.msg.start': { en: 'Billiards school — take your time, nothing here can be lost.', ko: '당구 교실 — 천천히 하세요. 여기선 잃을 게 없습니다.' },
  'tut.msg.passed': { en: 'Step cleared!', ko: '단계 통과!' },
  'tut.msg.done': { en: 'Course complete — you know every control now.', ko: '수료! 이제 모든 조작을 익혔습니다.' },
  // action-mission checklist rows
  'tut.action.aim:enter': { en: 'Left-click into aim mode', ko: '좌클릭으로 조준 모드 켜기' },
  'tut.action.view:orbit': { en: 'Right-click or right-drag to look around', ko: '우클릭(또는 드래그)으로 시야 둘러보기' },
  'tut.action.aim:exitkey': { en: 'Press any key to leave aim mode', ko: '아무 키나 눌러 조준 모드 끄기' },
  'tut.action.guide': { en: 'Toggle the aim guide', ko: '조준선 켜고 끄기' },
  'tut.action.chalk': { en: 'Chalk the tip', ko: '초크 바르기' },
  'tut.action.spin:drag': { en: 'Drag the tip to set spin', ko: '당점을 드래그해 회전 주기' },
  'tut.action.view:zoom': { en: 'Scroll the mouse wheel to zoom', ko: '마우스 휠로 줌 인/아웃' },
  'tut.action.aim:fine': { en: 'Hold Shift for a fine adjustment', ko: 'Shift를 눌러 세부 조절' },
  'tut.action.touch:aim': { en: 'Drag on the table to aim', ko: '테이블을 드래그해 조준하기' },
  'tut.action.touch:orbit': { en: 'Drag with two fingers to look around', ko: '두 손가락 드래그로 시야 둘러보기' },
  'tut.action.touch:zoom': { en: 'Pinch to zoom in/out', ko: '손가락으로 오므리고 벌려 줌 인/아웃' },
  'tut.done.control.title': { en: 'Controls learned', ko: '조작을 익혔습니다' },
  'tut.done.control.goal': { en: 'You can enter and leave aim mode, take a stroke, and read the aim guide.', ko: '조준 모드를 켜고 끄고, 스트로크를 치고, 조준선을 읽을 수 있습니다.' },
  'tut.done.control.goal.touch': { en: 'You can aim and look around the table, take a stroke, and read the aim guide.', ko: '테이블을 조준하고 둘러보고, 스트로크를 치고, 조준선을 읽을 수 있습니다.' },
  'tut.done.control.tip': { en: 'Next up: Basic carom skills covers pace and spin.', ko: '이어서 당구 기본기 과정에서 세기와 회전을 배워보세요.' },
  'tut.done.skills.title': { en: 'Basic skills learned', ko: '기본기를 익혔습니다' },
  'tut.done.skills.goal': { en: 'Pace, side spin, draw, follow and massé — you can shape where the cue ball ends up.', ko: '세기, 옆회전, 끌어치기, 밀어치기, 마세까지 — 수구를 원하는 대로 다룰 수 있습니다.' },
  'tut.done.skills.tip': { en: 'Next up: Advanced carom skills covers bank shots and scoring.', ko: '이어서 실전 응용 과정에서 빈쿠션과 득점을 배워보세요.' },
  'tut.done.advanced.title': { en: 'Advanced skills learned', ko: '실전 응용을 마쳤습니다' },
  'tut.done.advanced.goal': { en: 'Bank shots and scoring a carom — the whole game, start to finish.', ko: '빈쿠션과 득점까지, 실전에서 쓰는 기술을 모두 배웠습니다.' },
  'tut.done.advanced.tip': { en: 'Try Solo practice next, or 오늘의 배치 for a layout everyone plays that day.', ko: '이제 혼자 연습이나 오늘의 배치에 도전해 보세요.' },

  // 1. aim mode vs view mode — the split every other lesson assumes
  // (title/goal/keys for every built-in mission live in tutorial/lessonCopy.json,
  // merged into DICT below — edit that file, not this one, for lesson copy.)
  'tut.aimmode.tip': { en: 'The mouse does one of two jobs. In aim mode it moves the CUE, so the cursor disappears; otherwise it moves the CAMERA. If the controls ever seem stuck, press any key — that always hands the cursor back.', ko: '마우스는 두 가지 일 중 하나를 합니다. 조준 모드에서는 큐를 움직이고(커서가 사라져요), 아닐 때는 카메라를 움직입니다. 조작이 안 먹는 것 같으면 아무 키나 누르세요 — 언제나 커서가 돌아옵니다.' },

  // 1a. touch's mirror of aimmode — no mode switch, but the camera gestures
  // need teaching before the first real shot expects you to already know them.
  'tut.touchaim.tip': { en: 'One finger always aims the cue — there is no separate aim mode to switch into. Two fingers move the camera instead.', ko: '한 손가락은 언제나 큐를 조준합니다 — 따로 켜고 끄는 조준 모드가 없어요. 두 손가락은 대신 카메라를 움직입니다.' },

  // 1b. the rest of the mouse's jobs in aim mode
  'tut.mousecontrols.tip': { en: 'Fine adjustment matters most on long, thin shots, where a whole-speed turn overshoots the angle you want.', ko: '세부 조절은 얇게 맞춰야 하는 긴 거리 샷에서 특히 중요합니다 — 보통 속도로는 원하는 각도를 지나치기 쉬워요.' },

  // 2. first stroke
  'tut.stroke.touch': { en: 'Pull the stroke pad (bottom right) down, then release.', ko: '오른쪽 아래 스트로크 패드를 아래로 당겼다가 놓으세요.' },
  'tut.stroke.tip': { en: 'The stroke is a real swing: how FAST you push forward is how hard you hit.', ko: '스트로크는 실제 스윙입니다 — 앞으로 미는 속도가 곧 세기예요.' },
  'tut.stroke.retry': { en: 'Give it another go.', ko: '한 번 더 해보세요.' },

  // 3. aim guide
  'tut.guide.touch': { en: 'Tap the aim-guide button in the HUD.', ko: 'HUD의 조준선 버튼을 누르세요.' },
  'tut.guide.tip': { en: 'The pale circle is the "ghost ball" — put it where the cue ball must sit to make the contact you want.', ko: '반투명한 원이 고스트볼입니다 — 수구가 적구에 닿는 자리를 미리 보여줘요.' },

  // 4. aim at a ball
  'tut.aim.touch': { en: 'Drag across the table to aim — drag slowly for fine aim.', ko: '테이블을 드래그해 조준하세요 — 천천히 움직이면 미세 조준됩니다.' },
  'tut.aim.tip': { en: 'Aim at the ghost ball, not at the red itself.', ko: '적구가 아니라 고스트볼을 겨냥하세요.' },
  'tut.aim.retry': { en: 'That one missed the red. The balls are back where they started — try again.', ko: '적구를 못 맞혔어요. 공을 원래 자리로 되돌렸습니다 — 다시 해보세요.' },

  // 5. soft touch
  'tut.soft.touch': { en: 'Release the stroke pad after a short, slow pull.', ko: '스트로크 패드를 짧고 느리게 당겼다 놓으세요.' },
  'tut.soft.tip': { en: 'Position play is mostly pace. A soft ball stays where you left it.', ko: '포지션은 대부분 세기 싸움입니다. 약하게 친 공은 그 자리에 남아요.' },
  'tut.soft.retry': { en: 'A little softer — that was still a firm hit.', ko: '조금만 더 부드럽게 — 아직 셌어요.' },

  // 6. power (dropped from the curriculum — see missions.ts — copy kept for admin history)
  'tut.power.title': { en: 'Send it around', ko: '길게 보내기' },
  'tut.power.goal': { en: 'Hit hard enough that the white ball finds at least three cushions.', ko: '수구가 쿠션을 세 번 이상 돌 만큼 강하게 치세요.' },
  'tut.power.keys': { en: 'Big pull-back, then swing FORWARD fast.', ko: '크게 당긴 뒤 앞으로 빠르게 휘두르세요.' },
  'tut.power.touch': { en: 'Pull the stroke pad all the way down and flick it up.', ko: '스트로크 패드를 끝까지 당겼다가 빠르게 놓으세요.' },
  'tut.power.tip': { en: 'Full power is rarely the right answer in a game — but you need to know where it is.', ko: '실전에서 최대 세기를 쓸 일은 드물지만, 어디까지 가는지는 알아야 해요.' },
  'tut.power.retry': { en: 'Not quite three cushions yet — swing through it faster.', ko: '아직 쿠션 3번을 못 채웠어요 — 더 빠르게 밀어보세요.' },

  // 7. chalk
  'tut.chalk.touch': { en: 'Tap the chalk button in the HUD.', ko: 'HUD의 초크 버튼을 누르세요.' },
  'tut.chalk.tip': { en: 'Chalk wears down as you play, and spin wears it faster. A bare tip miscues — chalk before any shot with heavy english.', ko: '초크는 치면서 닳고, 회전을 줄수록 빨리 닳습니다. 팁이 마르면 미스큐가 나요 — 회전을 많이 줄 샷 전에는 꼭 바르세요.' },

  // 8. side spin
  'tut.side.touch': { en: 'Drag the spin pad in the HUD to move the red contact dot sideways.', ko: 'HUD의 당점 패드를 드래그해 빨간 점을 옆으로 옮기세요.' },
  'tut.side.tip': { en: "Side spin barely changes the first contact — it changes the ANGLE off the rail. Running english widens the rebound, reverse kills it.", ko: '옆 회전은 첫 접촉보다 쿠션에서의 각도를 바꿉니다. 순회전은 각을 넓히고, 역회전은 죽여요.' },
  'tut.side.retry': { en: 'Move the contact dot further to the side, and make sure the ball reaches a rail.', ko: '당점을 더 옆으로 옮기고, 공이 쿠션까지 가도록 쳐보세요.' },

  // 9. draw
  'tut.draw.tip': { en: 'Draw needs pace as well as a low tip — a soft low hit just rolls.', ko: '끌어치기는 낮은 당점만으론 안 되고 세기도 필요합니다 — 약하게 치면 그냥 굴러가요.' },
  'tut.draw.retry': { en: 'Get the contact dot lower on the ball, and still hit the red.', ko: '당점을 더 아래로 내리고, 적구도 맞혀 보세요.' },

  // 10. follow
  'tut.follow.tip': { en: 'Draw and follow are how you choose where the cue ball ends up — the whole game is the NEXT shot.', ko: '끌어치기와 밀어치기로 수구의 도착점을 고릅니다 — 당구는 결국 다음 샷 싸움이에요.' },
  'tut.follow.retry': { en: 'Get the contact dot higher on the ball, and still hit the red.', ko: '당점을 더 위로 올리고, 적구도 맞혀 보세요.' },

  // 11. massé / cue elevation (dropped from the curriculum — see missions.ts)
  'tut.masse.title': { en: 'Raise the cue (massé)', ko: '큐 세우기 (마세)' },
  'tut.masse.goal': { en: 'Take a shot with the cue tilted up at least 20°.', ko: '큐를 20도 이상 세운 채로 쳐보세요.' },
  'tut.masse.keys': { en: 'In aim mode, move the mouse UP to raise the butt of the cue (down levels it).', ko: '조준 모드에서 마우스를 위로 움직이면 큐가 세워집니다 (아래로 내리면 눕습니다).' },
  'tut.masse.touch': { en: 'Drag the tilt dial (right side) upward.', ko: '오른쪽 틸트 다이얼을 위로 드래그하세요.' },
  'tut.masse.tip': { en: 'A raised cue makes the ball CURVE. Raise it a lot and add side spin for a real massé; raise it steeply and the ball jumps.', ko: '큐를 세우면 공이 휘어집니다. 많이 세우고 옆 회전을 주면 마세, 아주 가파르게 세우면 점프샷이 돼요.' },
  'tut.masse.retry': { en: 'The cue was still too flat — raise it further before you stroke.', ko: '아직 큐가 눕혀져 있어요 — 더 세운 뒤에 치세요.' },

  // 12. bank / 빈쿠션
  'tut.bank.tip': { en: 'Bank shots are how you escape when the direct line is blocked — and in 4-ball they finish the game.', ko: '빈쿠션은 직접 길이 막혔을 때의 탈출구이고, 사구에서는 마지막 득점 수단이기도 합니다.' },
  'tut.bank.retry': { en: 'The white touched the red without a rail first. Aim at the cushion.', ko: '쿠션을 거치지 않고 바로 맞았어요. 쿠션을 겨냥해 보세요.' },

  // 13. first carom
  'tut.carom.tip': { en: 'In 4-ball, hitting the OTHER cue ball is a foul — only the two reds count.', ko: '사구에서 상대 수구(노란 공)를 맞히면 파울입니다 — 빨간 공 두 개만 유효해요.' },
  'tut.carom.retry': { en: 'Almost — you need both reds in the same shot. Try a thinner first contact.', ko: '아쉬워요 — 한 샷에 빨간 공 두 개를 모두 맞혀야 합니다. 첫 적구를 더 얇게 맞혀보세요.' },


  // --- 이번주의 미션 (weekly challenge, resets Monday; params: n, t, s, v, h, m) ---
  'mainmenu.daily': { en: "This Week's Mission", ko: '이번주의 미션' },
  'mainmenu.dailyHint': { en: "This week's mission", ko: '이번주 미션' },
  'mainmenu.onlinePlay': { en: 'Online Play', ko: '온라인 대전' },
  'mainmenu.createRoom': { en: 'Create room', ko: '방 만들기' },
  'mainmenu.openRooms': { en: 'Open rooms', ko: '열려있는 방' },
  'mainmenu.practicingCount': { en: '{n} practicing', ko: '{n}명 연습 중' },
  'mainmenu.tutorialCount': { en: '{n} in tutorial', ko: '{n}명 튜토리얼 중' },
  'mainmenu.kakaoLogin': { en: 'Kakao Login / Sign up', ko: '카카오 로그인 / 회원가입' },
  'mainmenu.generalLogin': { en: 'Login / Sign up', ko: '일반 로그인 / 회원가입' },
  'mainmenu.practiceMenu': { en: 'Practice', ko: '연습' },
  'mainmenu.practiceChoose': { en: 'Choose how to practice', ko: '연습 방식을 선택하세요' },
  'mainmenu.topRanks': { en: 'Ranking', ko: '랭킹' },
  'mainmenu.missionGoal': { en: '{t} caroms', ko: '{t}점 목표' },
  'daily.title': { en: "This week's mission", ko: '이번주의 미션' },
  'daily.sub': { en: 'Same position for everyone this week, {n} tries', ko: '이번주 모두 같은 배치 · {n}번 도전' },
  'daily.subUnlimited': { en: 'Same position for everyone this week, unlimited tries', ko: '이번주 모두 같은 배치 · 무제한 도전' },
  'daily.retryUnlimited': { en: 'Try again', ko: '다시 도전' },
  'daily.goal': { en: 'Run {t} caroms in as few strokes as you can', ko: '{t}점을 최소 타수로 치세요' },
  'daily.goalBanks': { en: 'Run {t} caroms — at least {b} must be bank shots', ko: '{t}점 중 {b}점 이상은 뱅크샷으로 성공하세요' },
  'daily.goalTimed': { en: 'Score as many caroms as you can in {s}s', ko: '{s}초 안에 최대한 많은 점수를 내세요' },
  'daily.goalSpeed': { en: 'Run {t} caroms as fast as you can', ko: '{t}점을 최대한 빨리 치세요' },
  'daily.play': { en: 'Play', ko: '도전' },
  'daily.retry': { en: 'Try again ({n} left)', ko: '다시 도전 ({n}회 남음)' },
  'daily.spent': { en: 'No attempts left this week', ko: '이번주 도전 횟수 소진' },
  'daily.best': { en: 'Your best: {n} strokes', ko: '내 기록: {n}타' },
  'daily.noClear': { en: 'Best so far: {n} caroms', ko: '최고 {n}점' },
  'daily.bestScore': { en: 'Your best: {n} caroms', ko: '내 기록: {n}점' },
  'daily.bestTime': { en: 'Your best: {s}s', ko: '내 기록: {s}초' },
  'daily.untried': { en: 'Not attempted yet', ko: '아직 도전 안 함' },
  'daily.cleared': { en: 'Cleared in {n} strokes!', ko: '{n}타 성공!' },
  'daily.clearedTime': { en: 'Cleared in {s}s!', ko: '{s}초에 성공!' },
  'daily.failed': { en: 'Run ended at {n} caroms', ko: '{n}점에서 종료' },
  'daily.timeUp': { en: "Time's up — {n} caroms", ko: '시간 종료 — {n}점' },
  'daily.board': { en: "This week's board — top 5 win coins", ko: '이번주 순위 — TOP 5 코인 지급' },
  'daily.strokes': { en: '{n}타', ko: '{n}타' },
  'daily.caroms': { en: '{n}점', ko: '{n}점' },
  'daily.time': { en: '{s}s', ko: '{s}초' },
  'daily.dnf': { en: 'DNF', ko: '미완' },
  'daily.empty': { en: 'Nobody has cleared it yet — go first', ko: '아직 아무도 성공 못했어요 — 1등 하세요' },
  'daily.quit': { en: 'Give up', ko: '포기' },
  'daily.rerollSoon': { en: 'Resets any moment', ko: '곧 초기화' },
  'daily.rerollMin': { en: 'Resets in {n}m', ko: '{n}분 후 초기화' },
  'daily.rerollHr': { en: 'Resets in {h}h {m}m', ko: '{h}시간 {m}분 후 초기화' },
  'daily.hud': { en: "이번주의 미션 · {a}/{t}점 · {n}타", ko: '이번주의 미션 · {a}/{t}점 · {n}타' },
  'daily.hudBanks': { en: '이번주의 미션 · {a}/{t}점 (뱅크 {b}/{m}) · {n}타', ko: '이번주의 미션 · {a}/{t}점 (뱅크 {b}/{m}) · {n}타' },
  'daily.hudTimed': { en: '이번주의 미션 · {a}점', ko: '이번주의 미션 · {a}점' },
  'daily.hudSpeed': { en: '이번주의 미션 · {a}/{t}점 · {e}초', ko: '이번주의 미션 · {a}/{t}점 · {e}초' },
  'daily.badgeStrokes': { en: '{n} strokes', ko: '{n}타' },
  'daily.badgeBanks': { en: 'bank {b}/{m}', ko: '뱅크 {b}/{m}' },
  'daily.badgeTimeLeft': { en: '{n}s left', ko: '{n}초 남음' },
  'daily.badgeElapsed': { en: '{n}s', ko: '{n}초' },
  'daily.badgeScore': { en: '{n} caroms', ko: '{n}점' },

  // --- head-to-head / rematch (params: w, l, name) ---
  'h2h.record': { en: 'vs {name} {w}승 {l}패', ko: 'vs {name} {w}승 {l}패' },
  'h2h.first': { en: 'First meeting', ko: '첫 대결' },
  'h2h.rematch': { en: 'Rematch', ko: '재대결' },
  'h2h.invited': { en: 'Rematch invite sent to {name}', ko: '{name}님에게 재대결 신청' },
  'h2h.invite': { en: '{name} wants a rematch', ko: '{name}님이 재대결을 신청했어요' },
  'h2h.accept': { en: 'Accept', ko: '수락' },
  'h2h.decline': { en: 'Decline', ko: '거절' },

  // --- shot sharing (params: name) ---
  'share.shot': { en: 'Share this shot', ko: '이 샷 공유' },
  'share.shared': { en: 'Shared to the room', ko: '방에 공유했어요' },
  'share.cardTitle': { en: 'Shared shot', ko: '공유한 샷' },
  'share.replay': { en: 'Replay', ko: '재생' },
  'share.needRoom': { en: 'Join a room to share', ko: '방에 들어가야 공유할 수 있어요' },
  'share.linkReady': { en: 'Anyone can watch this shot with the link:', ko: '이 링크로 누구나 이 샷을 볼 수 있어요:' },
  'share.copyLink': { en: 'Copy link', ko: '링크 복사' },
  'share.linkCopied': { en: 'Link copied', ko: '링크를 복사했어요' },
  'share.close': { en: 'Close', ko: '닫기' },
  'share.viewing': { en: 'Shared shot — {label}', ko: '공유된 샷 — {label}' },
  'share.viewingPlain': { en: 'Shared shot', ko: '공유된 샷' },
  'share.visitorPrompt': { en: 'Fancy this one yourself?', ko: '직접 쳐볼까요?' },
  'share.tryIt': { en: 'Try this shot', ko: '이 샷 쳐보기' },
  'share.watchAgain': { en: 'Watch again', ko: '다시 보기' },
  'share.busy': { en: 'Table is in use — try it after the game', ko: '테이블 사용 중 — 게임이 끝난 뒤에 보세요' },
  // skill assessment (calibration)
  'menu.assess': { en: 'Skill assessment', ko: '급수 측정' },
  'assess.start': { en: 'Assessment — score as many caroms as you can in {total} shots.', ko: '급수 측정 — {total}구 동안 최대한 많이 득점하세요.' },
  'assess.progress': { en: 'Shot {turn}/{total} · scored {scored} · level {level}', ko: '{turn}/{total}구 · 득점 {scored} · 다마 {level}' },
  'assess.result': { en: 'Assessment complete!<br>Level {level} · {rank}<br><span style="font-size:14px;opacity:.85">{scored}/{total} caroms</span>', ko: '측정 완료!<br>다마 {level} · {rank}<br><span style="font-size:14px;opacity:.85">{total}구 중 {scored}득점</span>' },
  'rank.rookie': { en: 'Rookie', ko: '입문' },
  'rank.beginner': { en: 'Beginner', ko: '초급' },
  'rank.intermediate': { en: 'Intermediate', ko: '중급' },
  'rank.advanced': { en: 'Advanced', ko: '상급' },
  'rank.expert': { en: 'Expert', ko: '고수' },
  'rank.master': { en: 'Master', ko: '선수급' },
  // accounts (optional sign-up)
  'auth.user': { en: 'Username', ko: '아이디' },
  'auth.pass': { en: 'Password', ko: '비밀번호' },
  'auth.signup': { en: 'Sign up', ko: '가입' },
  'auth.login': { en: 'Log in', ko: '로그인' },
  'auth.logout': { en: 'Log out', ko: '로그아웃' },
  'auth.signedIn': { en: '👤 {name}', ko: '👤 {name}' },
  // chat
  'chat.title': { en: 'Chat', ko: '채팅' },
  'chat.placeholder': { en: 'Message the room — Enter to send, /w <name> to whisper', ko: '방 전체에 메시지 — Enter 전송, /w <닉네임> 귓속말' },
  'chat.placeholderWhisper': { en: '/w <nickname> <message> to whisper', ko: '/w <닉네임> <메시지> 로 귓속말' },
  'chat.placeholderDm': { en: 'Private message — Enter to send', ko: '귓속말 — Enter 전송' },
  'chat.tabRoom': { en: 'Room', ko: '방' },
  'chat.tabWhisper': { en: 'Whisper', ko: '귓속말' },
  'chat.toPeer': { en: 'To {name}', ko: '{name}에게' },
  'chat.openHint': { en: 'Chat (Enter)', ko: '채팅 (Enter)' },
  'chat.noRoom': { en: 'Not in a room — only whispers work here: /w <nickname> <message>', ko: '방에 없어 전체 채팅은 안 됩니다 — 귓속말만 가능: /w <닉네임> <메시지>' },
  'chat.noReply': { en: 'No one to reply to yet.', ko: '답장할 상대가 아직 없습니다.' },
  'chat.help': { en: 'Enter opens chat · /w <nickname> <message> whispers · /r replies · Tab switches conversation · Esc closes', ko: 'Enter 채팅 열기 · /w <닉네임> <메시지> 귓속말 · /r 답장 · Tab 대화 전환 · Esc 닫기' },
  'chat.unknown': { en: 'Unknown command "/{name}" — try /help', ko: '알 수 없는 명령 "/{name}" — /help 를 입력해 보세요' },
  'chat.offline': { en: '{name} is not online.', ko: '{name}님은 접속 중이 아닙니다.' },
  'chat.flood': { en: 'Slow down — too many messages.', ko: '메시지를 너무 빨리 보내고 있습니다.' },
  'chat.disconnected': { en: 'Not connected — the message was not sent.', ko: '연결이 끊겨 메시지를 보내지 못했습니다.' },
  'chat.whisperBtn': { en: 'Whisper', ko: '귓속말' },
  'chat.send': { en: 'Send', ko: '전송' },
  'chat.joined': { en: '{name} joined', ko: '{name}님이 입장했습니다' },
  'chat.left': { en: '{name} left', ko: '{name}님이 나갔습니다' },
  'chat.gameStarted': { en: 'Game started', ko: '게임 시작' },
  'chat.finishRule': { en: 'Finish: {c} final 3-cushion carom(s), {b} bank required', ko: '마무리: 마지막 쓰리쿠션 {c}회, 그중 뱅크 {b}회 필요' },

  // music (YouTube playlist)
  'music.title': { en: '▶ Music', ko: '▶ 음악' },
  'music.placeholder': { en: 'YouTube link, ID, or playlist', ko: 'YouTube 링크 · ID · 재생목록' },
  'music.add': { en: 'Add', ko: '추가' },
  'music.searchPlaceholder': { en: 'Search YouTube', ko: 'YouTube 검색' },
  'music.searchBtn': { en: 'Search', ko: '검색' },
  'music.searching': { en: 'Searching…', ko: '검색 중…' },
  'music.searchFail': { en: 'Search failed — check the API key.', ko: '검색 실패 — API 키를 확인하세요.' },
  'music.noKey': { en: 'No YouTube API key configured.', ko: 'YouTube API 키가 없습니다.' },
  'music.loadingPlaylist': { en: 'Loading playlist…', ko: '재생목록 불러오는 중…' },
  'music.play': { en: '▶ Play', ko: '▶ 재생' },
  'music.pause': { en: '⏸ Pause', ko: '⏸ 일시정지' },
  'music.prev': { en: '⏮', ko: '⏮' },
  'music.next': { en: '⏭', ko: '⏭' },
  'music.volume': { en: 'Volume', ko: '볼륨' },
  'music.sync': { en: 'Sync to room', ko: '방에 동기화' },
  'music.intro': { en: 'Add YouTube videos to watch together.', ko: '함께 보려면 YouTube 영상을 추가하세요.' },
  'music.empty': { en: 'Playlist is empty — add a YouTube link.', ko: '재생목록이 비어 있습니다 — YouTube 링크를 추가하세요.' },
  'music.badLink': { en: 'Unrecognised link — paste a YouTube URL or video ID.', ko: '인식할 수 없는 링크 — YouTube URL이나 영상 ID를 넣으세요.' },
  'music.added': { en: 'Added to the playlist.', ko: '재생목록에 추가했습니다.' },
  'music.synced': { en: 'Synced the room to your player.', ko: '방을 내 재생에 맞췄습니다.' },
  'music.resize': { en: 'Drag to resize', ko: '드래그하여 크기 조절' },
  'music.delete': { en: 'Remove', ko: '삭제' },
  'music.up': { en: 'Up', ko: '위로' },
  'music.down': { en: 'Down', ko: '아래로' },

  // voice chat
  'voice.join': { en: '🎙 Voice', ko: '🎙 음성' },
  'voice.leave': { en: '🎙 Leave', ko: '🎙 나가기' },
  'voice.mute': { en: 'Mute', ko: '음소거' },
  'voice.unmute': { en: 'Unmute', ko: '음소거 해제' },
  'voice.status.off': { en: 'Voice off', ko: '음성 꺼짐' },
  'voice.status.waiting': { en: 'Waiting for opponent…', ko: '상대 대기 중…' },
  'voice.status.connecting': { en: 'Connecting…', ko: '연결 중…' },
  'voice.status.live': { en: 'Talking', ko: '통화 중' },
  'voice.status.failed': { en: 'Mic blocked / no connection', ko: '마이크 차단 / 연결 실패' },

  // tuning
  'tuning.title': { en: '⚙ Tuning', ko: '⚙ 설정' },
  'tuning.reset': { en: 'Reset all', ko: '모두 초기화' },
  'tune.slidingFriction': { en: 'Sliding friction', ko: '미끄럼 마찰' },
  'tune.rollingFriction': { en: 'Rolling friction', ko: '구름 마찰' },
  'tune.spinFriction': { en: 'Spin friction', ko: '회전 마찰' },
  'tune.ballRestitution': { en: 'Ball restitution', ko: '공 반발' },
  'tune.cushionRestitution': { en: 'Cushion restitution', ko: '쿠션 반발' },
  'tune.cushionFalloff': { en: 'Cushion falloff', ko: '쿠션 감쇠' },
  'tune.cushionMin': { en: 'Cushion min rest.', ko: '쿠션 최소 반발' },
  'tune.cushionFriction': { en: 'Cushion friction', ko: '쿠션 마찰' },
  'tune.cushionRollGrip': { en: 'Cushion roll-grip', ko: '쿠션 구름' },
  'tune.followDraw': { en: 'Follow / draw spin', ko: '끌어치기/밀어치기' },
  'tune.masse': { en: 'Massé / swerve', ko: '마세(휘어치기)' },
  'tune.jump': { en: 'Jump strength', ko: '점프 강도' },
  'tune.gravity': { en: 'Gravity', ko: '중력' },
  'tune.maxSpeed': { en: 'Max shot speed', ko: '최대 샷 속도' },
  'tune.animSpeed': { en: 'Animation speed', ko: '애니메이션 속도' },
  'tune.aimSens': { en: 'Aim sensitivity', ko: '조준 감도' },
  'tune.tiltSens': { en: 'Tilt sensitivity', ko: '기울기 감도' },
  'tune.strokeRange': { en: 'Stroke range (px)', ko: '스트로크 범위 (px)' },
  'tune.shotJitter': { en: 'Shot variation', ko: '샷 편차' },
  'tune.fov': { en: 'Field of view (lower = flatter)', ko: '시야각 (낮을수록 평평)' },
  'tune.zoomOut': { en: 'Max zoom out', ko: '최대 줌아웃' },
  'tune.zoomIn': { en: 'Max zoom in (lower = closer)', ko: '최대 줌인 (낮을수록 가까이)' },
  'tune.zoomRate': { en: 'Zoom step rate', ko: '줌 속도' },

  // in-game messages (params vary)
  'msg.soloStart': { en: 'Solo practice — clear {h} points. Left-click to enter aim mode, hold {mod} and pull back to shoot.', ko: '혼자 연습 — {h}점을 비우세요. 좌클릭으로 조준 모드, {mod}를 누른 채 뒤로 당겨 치세요.' },
  'msg.onlineMode': { en: 'Online — create or join a room.', ko: '온라인 — 방을 만들거나 입장하세요.' },
  'msg.practice': { en: 'Waiting room — practice freely until everyone is ready.', ko: '대기실 — 모두 준비될 때까지 자유 연습.' },
  'msg.white': { en: 'White', ko: '흰공' },
  'msg.yellow': { en: 'Yellow', ko: '노란공' },
  'msg.breakArm': { en: 'Press any key or click to start the break', ko: '아무 키나 누르거나 클릭하여 브레이크를 시작하세요' },
  'msg.breakSweep': { en: 'Break! Move the mouse to swing the cue around the corner through the line of balls.', ko: '브레이크! 마우스를 움직여 큐를 모서리 중심으로 휘둘러 일렬의 공을 쳐 흩뜨리세요.' },
  'msg.breakWatch': { en: 'Opponent is breaking…', ko: '상대가 브레이크 중…' },
  'prompt.oppLeftWin': { en: '🎉 Opponent left — you win!<br><span style="font-size:13px;opacity:.8">Keep playing on your own, or exit?</span>', ko: '🎉 상대가 나갔습니다 — 승리!<br><span style="font-size:13px;opacity:.8">혼자 계속 하시겠어요, 아니면 나가시겠어요?</span>' },
  'prompt.keepPlaying': { en: 'Keep playing', ko: '계속 하기' },
  'prompt.toMenu': { en: 'Main menu', ko: '메인 메뉴' },
  'prompt.specMatchEnded': { en: '🏆 {name} won!<br><span style="font-size:13px;opacity:.8">Watch the next match, or exit?</span>', ko: '🏆 {name}님 승리!<br><span style="font-size:13px;opacity:.8">다음 경기를 볼까요, 아니면 나가시겠어요?</span>' },
  'prompt.specMatchEndedDraw': { en: 'Match ended in a draw.<br><span style="font-size:13px;opacity:.8">Watch the next match, or exit?</span>', ko: '무승부로 종료되었습니다.<br><span style="font-size:13px;opacity:.8">다음 경기를 볼까요, 아니면 나가시겠어요?</span>' },
  'prompt.watchNext': { en: 'Watch next match', ko: '다음 경기 보기' },
  'prompt.exitSpectate': { en: 'Exit', ko: '나가기' },
  'msg.chalked': { en: 'Tip chalked — full grip.', ko: '팁에 초크 — 그립 최대.' },
  'msg.miscue': { en: 'Miscue! The tip skidded off.', ko: '미스큐! 팁이 미끄러졌습니다.' },
  'msg.aiStart': { en: 'Vs AI — clear {h} points first. Left-click to aim, hold {mod} and pull back to shoot.', ko: 'AI 대전 — 먼저 {h}점을 비우세요. 좌클릭으로 조준, {mod}를 누른 채 뒤로 당겨 치세요.' },
  'msg.youBreak': { en: 'Connected — you break.', ko: '연결됨 — 당신이 선공입니다.' },
  'msg.oppBreaks': { en: 'Connected — opponent breaks.', ko: '연결됨 — 상대가 선공입니다.' },
  'msg.oppLeft': { en: 'Opponent disconnected — waiting for them to reconnect…', ko: '상대 연결 끊김 — 재접속을 기다리는 중…' },
  'msg.oppBack': { en: 'Opponent reconnected.', ko: '상대가 재접속했습니다.' },
  'msg.stakeWin': { en: 'You won the {n} 코인 stake!', ko: '{n} 코인 내기에서 이겼습니다!' },
  'msg.stakeLoss': { en: 'You lost the {n} 코인 stake.', ko: '{n} 코인 내기에서 졌습니다.' },
  'msg.reconnecting': { en: 'Reconnecting… ({n}/5)', ko: '재접속 중… ({n}/5)' },
  'msg.timeout': { en: "{name}'s shot clock ran out — turn passed.", ko: '{name}님 제한시간 초과 — 턴이 넘어갑니다.' },
  'msg.timeoutForfeit': { en: '{name} timed out 3 times in a row and lost the game.', ko: '{name}님이 3회 연속 시간 초과로 패배했습니다.' },
  'msg.disconnected': { en: 'Disconnected.', ko: '연결이 끊겼습니다.' },
  'msg.roomClosed': { en: 'Room closed — opponent left.', ko: '방이 닫혔습니다 — 상대가 나갔습니다.' },
  'msg.roomClosedSpectator': { en: 'The match ended — room closed.', ko: '경기가 종료되어 방이 닫혔습니다.' },
  'msg.kicked': { en: 'You were removed from the room by the host.', ko: '방장에 의해 방에서 내보내졌습니다.' },
  'msg.spectating': { en: 'Spectating.', ko: '관전 중.' },
  'msg.reconnected': { en: 'Reconnected.', ko: '재접속됨.' },
  'msg.strokeLoad': { en: 'Pull the mouse back to wind up, then swing it forward through the start line — the faster you swing, the harder the hit.', ko: '마우스를 뒤로 당겨 백스윙한 뒤, 시작선을 지나 앞으로 휘두르세요 — 빠르게 휘두를수록 강하게 칩니다.' },
  'msg.strokeCancel': { en: 'Stroke cancelled — hold {mod}, pull back, then push forward to shoot.', ko: '스트로크 취소 — {mod}를 누르고 뒤로 당겼다가 앞으로 밀어 치세요.' },
  'msg.aimMode': { en: 'Aim mode — move the mouse to aim. Right-click or any key to exit.', ko: '조준 모드 — 마우스로 조준하세요. 우클릭이나 아무 키로 종료.' },
  'msg.aimExit': { en: 'Aim mode off.', ko: '조준 모드 꺼짐.' },
  'msg.aimRelock': { en: 'Click the table to lock the cursor for aiming.', ko: '조준을 위해 테이블을 클릭해 커서를 고정하세요.' },
  'avatar.title': { en: 'Edit avatar', ko: '아바타 편집' },
  'avatar.edit': { en: 'Edit avatar', ko: '아바타 편집' },
  'avatar.randomize': { en: 'Randomize', ko: '무작위' },
  'avatar.save': { en: 'Save', ko: '저장' },
  'avatar.cancel': { en: 'Cancel', ko: '취소' },
  'avatar.face': { en: 'Face shape', ko: '얼굴형' },
  'avatar.hair': { en: 'Hair', ko: '머리' },
  'avatar.hairColor': { en: 'Hair colour', ko: '머리색' },
  'avatar.brows': { en: 'Eyebrows', ko: '눈썹' },
  'avatar.eyes': { en: 'Eyes', ko: '눈' },
  'avatar.nose': { en: 'Nose', ko: '코' },
  'avatar.mouth': { en: 'Mouth', ko: '입' },
  'avatar.clothing': { en: 'Clothing', ko: '의상' },
  'avatar.clothingColor': { en: 'Clothing colour', ko: '의상색' },
  'avatar.skin': { en: 'Skin', ko: '피부' },
  'avatar.bg': { en: 'Background', ko: '배경' },
  'exit.confirm': { en: 'Leave this game and return to the menu?', ko: '이 게임을 나가고 메뉴로 돌아갈까요?' },
  'exit.onlineWarn': { en: '⚠ Leaving this online match forfeits it and will deduct rank points. Leave anyway?', ko: '⚠ 온라인 경기를 떠나면 몰수패 처리되어 랭크 점수가 차감됩니다. 그래도 나갈까요?' },
  'exit.leave': { en: 'Leave', ko: '나가기' },
  'exit.stay': { en: 'Stay', ko: '계속하기' },
  'msg.reviewing': { en: 'Reviewing — move the mouse over the table to resume.', ko: '리뷰 중 — 테이블 위에서 마우스를 움직이면 계속됩니다.' },
  'msg.resetShot': { en: 'Reset to the last shot — play it again.', ko: '직전 샷으로 되돌림 — 다시 쳐보세요.' },
  'msg.zoom': { en: 'View zoom: {z}×', ko: '화면 줌: {z}×' },
  'msg.zoomOrbit': { en: 'Use the mouse wheel to zoom in orbit view.', ko: '자유 시점에서는 마우스 휠로 줌하세요.' },
  'msg.power': { en: '{p}% power', ko: '세기 {p}%' },

  // shot results (params: n, m, ball)
  'shot.carom': { en: 'Carom!', ko: '득점!' },
  'shot.caromCushions': { en: 'Carom! {n} cushions', ko: '득점! {n}쿠션' },
  'shot.missNothing': { en: 'Missed — cue ball hit nothing', ko: '실패 — 헛쳤습니다' },
  'shot.missSecond': { en: 'Missed the second ball', ko: '실패 — 두 번째 공을 못 맞췄습니다' },
  'shot.missCushions': { en: 'Only {n} of {m} cushions', ko: '{m}쿠션 중 {n}쿠션' },
  'shot.foulOff': { en: 'Foul: a ball left the table', ko: '파울: 공이 테이블 밖으로' },
  'shot.foulBreak': { en: 'Foul: must strike the {ball} first on the break', ko: '파울: 선공은 {ball}을 먼저 맞춰야 합니다' },
  'shot.foulNothing': { en: 'Foul: cue ball hit nothing', ko: '파울: 헛쳤습니다' },
  'ball.red': { en: 'red', ko: '빨강' },
  'ball.white': { en: 'white', ko: '흰공' },
  'ball.yellow': { en: 'yellow', ko: '노랑' },
};

/**
 * 당구 교실 lesson copy — title/goal/keys for every BUILT-IN mission — lives in
 * tutorial/lessonCopy.json instead of the literal above, so it can be edited
 * (in both languages, side by side) without touching this file's TypeScript.
 * An admin-authored override (see tutorial/runtime.ts) can still replace any of
 * these live via `setDictEntry`, same as before.
 */
for (const [id, copy] of Object.entries(lessonCopy as Record<string, Record<string, { en: string; ko: string }>>)) {
  for (const [part, entry] of Object.entries(copy)) DICT[`tut.${id}.${part}`] = entry;
}

let lang: Lang = 'ko'; // default to Korean
const subs = new Set<() => void>();

export function getLang(): Lang {
  return lang;
}

export function setLang(l: Lang): void {
  if (l === lang) return;
  lang = l;
  for (const fn of subs) fn();
}

/** Translate a key, substituting {param} placeholders. */
export function t(key: string, params?: Record<string, string | number>): string {
  const entry = DICT[key];
  let s = entry ? entry[lang] : key;
  if (s.includes('{mod}')) s = s.split('{mod}').join(MOD_KEY);
  if (params) for (const k of Object.keys(params)) s = s.split(`{${k}}`).join(String(params[k]));
  return s;
}

/**
 * Is there a translation for this key? Lets a caller fall back deliberately
 * (e.g. a mission with no touch-specific hint reuses its keyboard one) instead
 * of rendering the raw key, which is what `t` returns for a miss.
 */
export function hasKey(key: string): boolean {
  return key in DICT;
}

/** Read both languages of a dictionary entry directly — for tools (the admin
 *  console) that need the actual copy rather than whatever the live language
 *  switch currently shows. */
export function entryOf(key: string): { en: string; ko: string } | undefined {
  return DICT[key];
}

/**
 * Register or override one dictionary entry at runtime — how admin-authored
 * tutorial copy (see src/tutorial/runtime.ts) joins the same `tut.<id>.*` keys
 * the built-in curriculum uses, so every existing `t()`/`tText()` call site
 * picks it up with no special-casing.
 */
export function setDictEntry(key: string, en: string, ko: string): void {
  DICT[key] = { en, ko };
}

/** Run `fn` now and again on every language change (for static labels). */
export function onLang(fn: () => void): void {
  subs.add(fn);
  fn();
}

/** Bind an element's text to a key, kept in sync across language changes. */
export function tText(el: HTMLElement, key: string): void {
  onLang(() => (el.textContent = t(key)));
}
